/**
 * Shared fixture/results stage tabs used by FixturesPage and ResultsPage.
 * Not a standalone route (/scores redirects to /fixtures).
 */
import {
  Box,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useBreakpointValue,
  useColorModeValue,
} from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { SITE_NAV_STICKY_HEIGHT } from '@/components/Navbar/siteNavLayout';
import { useLiveMatches } from '@/hooks/useLiveMatches';
import type { Fixture } from '@/models/fixture';
import type { gameResult } from '@/models/gameUpdate';
import { TournamentStagePanel } from './TournamentStagePanel';
import { TOURNAMENT_STAGE_TABS } from './scoresPageTabs';
import { getDefaultStageTabIndex, partitionByStageTab } from './scoresPageUtils';

function resetStagePanelScroll(feedRoot: HTMLElement | null) {
  if (!feedRoot) return;
  feedRoot.querySelectorAll('[data-stage-panel-scroll]').forEach((el) => {
    if (el instanceof HTMLElement) {
      el.scrollTop = 0;
    }
  });
}

export function TournamentStagePageContent(props: {
  variant: 'fixtures' | 'results';
  fixtures: Fixture[];
  gameResults: gameResult[];
}) {
  const { variant, fixtures, gameResults } = props;
  const liveMatches = useLiveMatches();
  const scrollContained = useBreakpointValue({ base: false, lg: true }) ?? false;
  const cardRadius = useBreakpointValue({ base: 16, lg: 0 });
  const useShortTabLabels = useBreakpointValue({ base: true, md: false }) ?? true;
  const stickyBarBg = useColorModeValue('white', 'bgBlue');

  const defaultTabIndex = useMemo(
    () => getDefaultStageTabIndex(fixtures, gameResults),
    [fixtures, gameResults],
  );
  const [tabIndex, setTabIndex] = useState(0);
  const hasUserChangedTab = useRef(false);
  const feedTopRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasUserChangedTab.current) {
      setTabIndex(defaultTabIndex);
    }
  }, [defaultTabIndex]);

  const tabData = useMemo(
    () => partitionByStageTab(fixtures, gameResults, liveMatches),
    [fixtures, gameResults, liveMatches],
  );

  function handleTabChange(index: number) {
    hasUserChangedTab.current = true;
    setTabIndex(index);
    requestAnimationFrame(() => {
      feedTopRef.current?.scrollIntoView({ block: 'start' });
      resetStagePanelScroll(feedTopRef.current);
    });
  }

  return (
    <Box ref={feedTopRef} w='full' scrollMarginTop={SITE_NAV_STICKY_HEIGHT}>
      <Tabs
        index={tabIndex}
        onChange={handleTabChange}
        variant='line'
        colorScheme='blue'
        w='full'
        isFitted
      >
        <Box
          position='sticky'
          top={SITE_NAV_STICKY_HEIGHT}
          zIndex={9}
          w='full'
          py={1}
          bg={stickyBarBg}
        >
          <TabList w='full'>
            {TOURNAMENT_STAGE_TABS.map((tab) => (
              <Tab
                key={tab.id}
                flex={1}
                minW={0}
                px={1}
                justifyContent='center'
                fontSize={{ base: 'xs', sm: 'sm', md: 'md' }}
                whiteSpace='nowrap'
              >
                {useShortTabLabels ? tab.shortLabel : tab.label}
              </Tab>
            ))}
          </TabList>
        </Box>
        <TabPanels>
          {TOURNAMENT_STAGE_TABS.map((tab, index) => (
            <TabPanel key={tab.id} px={0} pt={4}>
              <TournamentStagePanel
                variant={variant}
                fixturesForDisplay={tabData[index]!.fixturesForDisplay}
                liveMatches={tabData[index]!.liveMatches}
                gameResults={tabData[index]!.results}
                scrollContained={scrollContained}
                cardRadius={cardRadius}
              />
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Box>
  );
}
